import { useEffect, useMemo, useState } from 'react';
import { Loader2, Skull } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

/**
 * SAB-288 Phase 6 — Survivor side-game panel.
 *
 * Per TWEAK_DEFAULTS.survivorVariant = "list": renders the eliminated /
 * alive list with a red "Survivor Prize" header. Tombstones + bracket
 * variants are intentionally NOT shipped (kept commented out at the
 * bottom of this file as a future A/B reference).
 *
 * Reads league_survivor_config + survivor_eliminations directly via
 * supabase-js. RLS gates league-membership visibility (Phase 1).
 */

export interface SurvivorPanelTeam {
  id: string;
  name: string;
  owner_id: string;
}

interface SurvivorConfigRow {
  enabled: boolean;
  start_week: number;
  end_week: number;
}

interface EliminationRow {
  team_id: string;
  eliminated_week: number;
  fantasy_score: number | string | null;
}

const TEAM_COLOR_PALETTE = [
  '#F97316', '#EF4444', '#22C55E', '#3B82F6', '#A855F7',
  '#EAB308', '#EC4899', '#14B8A6', '#F472B6', '#94A3B8',
];

function hashId(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i++) {
    h = (h << 5) - h + s.charCodeAt(i);
    h |= 0;
  }
  return Math.abs(h);
}

function teamAvatar(name: string): string {
  const cleaned = name.replace(/[^A-Za-z0-9]/g, '');
  return (cleaned.slice(0, 2) || 'TM').toUpperCase();
}

function teamColor(id: string): string {
  return TEAM_COLOR_PALETTE[hashId(id) % TEAM_COLOR_PALETTE.length];
}

function fmtUSD(n: number): string {
  return '$' + n.toLocaleString('en-US', {
    minimumFractionDigits: n % 1 === 0 ? 0 : 2,
    maximumFractionDigits: 2,
  });
}

export function SurvivorPanel({
  leagueId,
  teams,
}: {
  leagueId: string;
  teams: SurvivorPanelTeam[];
}) {
  const [config, setConfig] = useState<SurvivorConfigRow | null>(null);
  const [elims, setElims] = useState<EliminationRow[]>([]);
  // SAB-632 migration 20 dropped league_survivor_config.bonus_amount; the
  // survivor prize now lives on the auto_survivor league_bonuses row.
  const [prizeAmount, setPrizeAmount] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      const [cfgRes, elimRes, prizeRes] = await Promise.all([
        supabase
          .from('league_survivor_config' as any)
          .select('enabled, start_week, end_week')
          .eq('league_id', leagueId)
          .maybeSingle(),
        supabase
          .from('survivor_eliminations' as any)
          .select('team_id, eliminated_week, fantasy_score')
          .eq('league_id', leagueId)
          .order('eliminated_week'),
        supabase
          .from('league_bonuses' as any)
          .select('amount')
          .eq('league_id', leagueId)
          .eq('trigger_type', 'auto_survivor')
          .maybeSingle(),
      ]);
      if (cancelled) return;
      setConfig(((cfgRes as any).data as SurvivorConfigRow | null) ?? null);
      setElims(((elimRes as any).data as EliminationRow[]) ?? []);
      setPrizeAmount(Number((prizeRes as any).data?.amount ?? 0));
      setLoading(false);
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [leagueId]);

  const elimByTeam = useMemo(() => {
    const m = new Map<string, EliminationRow>();
    for (const e of elims) m.set(e.team_id, e);
    return m;
  }, [elims]);

  if (loading) {
    return (
      <div className="rounded-xl border border-border bg-card p-8 flex items-center justify-center text-muted-foreground gap-2">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading Survivor…
      </div>
    );
  }

  if (!config?.enabled) {
    return (
      <div className="rounded-xl border border-dashed border-border bg-card p-8 text-center text-sm text-muted-foreground">
        <Skull className="w-7 h-7 mx-auto mb-2 text-muted-foreground/50" />
        Survivor Mode is off this season.
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* Prize card */}
      <div
        className={cn(
          'rounded-xl p-4 border',
          'bg-gradient-to-br from-red-500/8 to-transparent border-red-500/25',
        )}
      >
        <div className="text-[11px] font-bold uppercase tracking-[0.06em] text-red-400">
          Survivor Prize
        </div>
        <div className="font-bold text-3xl mt-1 tabular-nums tracking-tight">
          {fmtUSD(prizeAmount)}
        </div>
        <div className="text-[11px] text-muted-foreground mt-0.5">
          last team standing · weeks {config.start_week}–{config.end_week}
        </div>
      </div>

      {/* List variant — eliminated teams strikethrough+dimmed, alive teams bright */}
      <SurvivorList teams={teams} elimByTeam={elimByTeam} />
    </div>
  );
}

function SurvivorList({
  teams,
  elimByTeam,
}: {
  teams: SurvivorPanelTeam[];
  elimByTeam: Map<string, EliminationRow>;
}) {
  if (teams.length === 0) {
    return (
      <div className="rounded-xl border border-dashed border-border bg-card p-6 text-center text-sm text-muted-foreground">
        No teams in this league yet.
      </div>
    );
  }

  // Alive first, then eliminated by week ascending so most-recent elim sits
  // closest to the alive group.
  const sortedTeams = [...teams].sort((a, b) => {
    const ea = elimByTeam.get(a.id);
    const eb = elimByTeam.get(b.id);
    if (!ea && !eb) return a.name.localeCompare(b.name);
    if (!ea) return -1;
    if (!eb) return 1;
    return ea.eliminated_week - eb.eliminated_week;
  });

  return (
    <div className="rounded-xl border border-border bg-card overflow-hidden">
      {sortedTeams.map((t, i) => {
        const elim = elimByTeam.get(t.id);
        return (
          <div
            key={t.id}
            className={cn(
              'grid items-center gap-2.5 px-3.5 py-3',
              'grid-cols-[28px_1fr_auto]',
              i > 0 && 'border-t border-border/60',
              elim && 'opacity-50',
            )}
          >
            <div
              className="w-7 h-7 rounded-full flex items-center justify-center font-bold text-[10px]"
              style={{ background: teamColor(t.id), color: '#0B1322' }}
            >
              {teamAvatar(t.name)}
            </div>
            <div
              className={cn(
                'font-bold text-sm truncate',
                elim && 'line-through',
              )}
            >
              {t.name}
            </div>
            {elim ? (
              <Badge
                variant="outline"
                className="text-[9px] tracking-wider uppercase bg-red-500/12 text-red-400 border-red-500/30 h-5"
              >
                Out wk {elim.eliminated_week}
              </Badge>
            ) : (
              <Badge
                variant="outline"
                className="text-[9px] tracking-wider uppercase bg-emerald-500/12 text-emerald-400 border-emerald-500/30 h-5"
              >
                Alive
              </Badge>
            )}
          </div>
        );
      })}
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────────────
   Future A/B variants. Per TWEAK_DEFAULTS = "list", only the SurvivorList
   variant is rendered today. Tombstones + bracket are kept here as
   reference shapes for a future commissioner-toggle experiment.

   - Tombstones (graveyard): eliminated teams as RIP cards in a 3-col grid
     with team color + name + week of death + that-week's fantasy score.
   - Bracket: per-week rows showing eliminations as red-bordered chips in
     the matching week's slot, with current week highlighted in orange.

   When the experiment lands, we'd expose `variant: 'list' | 'tombstones'
   | 'bracket'` as a SurvivorPanel prop and switch on it inside this file.
   See _design-spec/handoff/sab-288/mobile.jsx SurvivorViz / SurvivorBracket
   for the source shapes.
   ────────────────────────────────────────────────────────────────────── */
